/* eslint-disable react/button-has-type */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { GripVertical, X, FileText, Save, RotateCcw } from 'lucide-react';
import adminServices from '../../../services/adminServices';

const LENGTH_TABS = ['Chapter Test', 'Subject Test', 'Full Test'];

const statusClass = (status) => {
  if (status === 'ACTIVE') return 'bg-green-100 text-green-800';
  if (status === 'INACTIVE') return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

const AssessmentOrderModal = ({ open, onClose, categoryList = [], onSaved }) => {
  const [activeTab, setActiveTab] = useState(LENGTH_TABS[0]);
  const [categoryId, setCategoryId] = useState('');
  const [items, setItems] = useState([]);
  const [initialIds, setInitialIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const isDirty = items.map((i) => i.id).join(',') !== initialIds.join(',');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await adminServices.getAssessments('', '', '', 1, 1000, categoryId, activeTab);
      const list = result?.data?.data || [];
      const sorted = [...list].sort(
        (a, b) => (a.display_order ?? Number.MAX_SAFE_INTEGER) - (b.display_order ?? Number.MAX_SAFE_INTEGER)
      );
      setItems(sorted);
      setInitialIds(sorted.map((i) => i.id));
    } catch (e) {
      console.error('Error fetching assessments for ordering:', e);
      setItems([]);
      setInitialIds([]);
      setError('Could not load assessments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, categoryId]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  useEffect(() => {
    if (!open) {
      setActiveTab(LENGTH_TABS[0]);
      setCategoryId('');
      setItems([]);
      setInitialIds([]);
      setError('');
    }
  }, [open]);

  const confirmDiscard = () => !isDirty || window.confirm('You have unsaved order changes. Discard them?');

  const handleTabChange = (tab) => {
    if (tab === activeTab || !confirmDiscard()) return;
    setActiveTab(tab);
  };

  const handleCategoryChange = (e) => {
    if (!confirmDiscard()) return;
    setCategoryId(e.target.value);
  };

  const handleClose = () => {
    if (!confirmDiscard()) return;
    onClose();
  };

  const handleReset = () => {
    const byId = Object.fromEntries(items.map((i) => [i.id, i]));
    setItems(initialIds.map((id) => byId[id]).filter(Boolean));
  };

  // ---- drag & drop (native HTML5, no extra dependency) ----
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overIndex !== index) setOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    const body = {
        assessment_length: activeTab,
        category: categoryId || null,
        order: items.map((item, index) => ({ id: item.id, display_order: index + 1 })),
      }
    try {
      const result = await adminServices.updateAssessmentOrder(body);
      if (result && (result.status === 200 || result.ok)) {
        setInitialIds(items.map((i) => i.id));
        if (onSaved) onSaved(`${activeTab} order saved successfully`);
        handleClose()
      } else {
        setError('Could not save the order. Please try again.');
      }
    } catch (e) {
      console.error('Error saving assessment order:', e);
      setError('Could not save the order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderList = () => {
    if (loading) {
      return <p className="py-10 text-center text-sm text-gray-500">Loading assessments...</p>;
    }
    if (items.length === 0) {
      return (
        <p className="py-10 text-center text-sm text-gray-500">
          No {activeTab} assessments{categoryId ? ' in this category' : ''} yet.
        </p>
      );
    }
    return (
      <ul id="assessment-order-list" className="space-y-2">
        {items && items?.length > 0 && items?.map((item, index) => {
          const isDragging = dragIndex === index;
          const isOver = overIndex === index && dragIndex !== null && dragIndex !== index;
          return (
            <li
              key={item.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 bg-white border rounded-lg px-3 py-2.5 cursor-grab active:cursor-grabbing transition-colors duration-100 ${
                isDragging ? 'opacity-40' : ''
              } ${isOver ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'}`}
            >
              <GripVertical className="w-5 h-5 text-gray-400 shrink-0" />
              <span className="w-7 text-sm font-medium text-gray-500 shrink-0">{index + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{item.title || '-'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1.5 truncate">
                  <FileText size={12} className="text-blue-600 shrink-0" />
                  {item.assessment_pattern_name || '-'}
                  <span>•</span>
                  <span className="truncate">{item.type || '-'}</span>

                 {item?.assessment_source_subtype && 
                 <>
                 <span>•</span>
                  <span className="truncate">{item?.assessment_source_subtype || '-'}</span>
                  </>
                  }
                  {Array.isArray(item?.batch_names) && item.batch_names.length > 0 && (
  <div className="mt-1 flex flex-wrap gap-1">
    {item.batch_names.map((name) => (
      <span
        key={name}
        className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium"
      >
        {name}
      </span>
    ))}
  </div>
)}
                </p>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${statusClass(item.status)}`}
              >
                {item.status === 'ON_MAINTENANCE' ? 'MAINTENANCE' : item.status}
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  if (!open) return null;

  // Rendered in a portal on document.body so the app's top bar / layout
  // stacking contexts can't overlap or clip the modal.
  return ReactDOM.createPortal(
    <div
      id="assessment-order-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40 p-4"
    >
      <div
        id="assessment-order-modal"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4" style={{ flexShrink: 0 }}>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Arrange assessment order</h3>
            <p className="mt-1 text-sm text-gray-500">
              Drag assessments to set the order students see them in. Each test length has its own order.
            </p>
          </div>
          <button
            id="assessment-order-close-btn"
            type="button"
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg shrink-0"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200" style={{ flexShrink: 0 }}>
          <div role="tablist" style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {LENGTH_TABS.map((tab) => (
              <button
                key={tab}
                id={`assessment-order-tab-${tab.replace(/\s/g, '-').toLowerCase()}`}
                role="tab"
                aria-selected={activeTab === tab}
                type="button"
                onClick={() => handleTabChange(tab)}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Category filter - own row so it can never overflow the modal */}
        {categoryList.length > 0 && (
          <div
            className="px-6 py-3 bg-white border-b border-gray-200"
            style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flexShrink: 0 }}
          >
            <label
              htmlFor="assessment-order-category-select"
              className="text-sm font-medium text-gray-700"
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minWidth: 0 }}
            >
              <span style={{ flexShrink: 0 }}>Category</span>
              <select
                id="assessment-order-category-select"
                value={categoryId}
                onChange={handleCategoryChange}
                style={{ boxSizing: 'border-box', width: '100%', maxWidth: 260, minWidth: 0, textOverflow: 'ellipsis' }}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
                <option value="">All Category</option>
                {categoryList.map((c) => (
                  <option key={c.id} value={c.id} title={c.code}>
                    {c.code && c.code.length > 28 ? `${c.code.slice(0, 28)}…` : c.code}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50" style={{ minHeight: 0 }}>
          {renderList()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-white" style={{ flexShrink: 0 }}>
          <p className={`text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
            {error || (isDirty ? 'Unsaved changes' : `${items.length} assessment${items.length === 1 ? '' : 's'}`)}
          </p>
          <div className="flex gap-3">
            <button
              id="assessment-order-reset-btn"
              type="button"
              disabled={!isDirty || saving}
              onClick={handleReset}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </button>
            <button
              id="assessment-order-save-btn"
              type="button"
              disabled={!isDirty || saving}
              onClick={handleSave}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save order'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssessmentOrderModal;